import { useRef, useLayoutEffect, useCallback, useMemo } from "react";
import { gsap } from "gsap";

export const useImageRotationAnimation = ({ images, logoUrl }) => {
  const bannerRef = useRef(null);
  const behindLayerRef = useRef(null);
  const frontLayerRef = useRef(null);
  const wrapsRef = useRef([]);
  const cardsRef = useRef([]);
  const timelineRef = useRef(null);

  // ✅ Prepare images
  const animationImages = useMemo(() => {
    if (!images || images.length < 4) return [];
    return images.slice(0, 4).map((img) => ({
      src: img.media,
      alt: img.name || "Animation image",
    }));
  }, [images]);

  //  FIXED SLOT POSITIONS
const getSlots = useCallback(() => {
  if (!bannerRef.current) return [];

  const rect = bannerRef.current.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;

  const isMobile = w <= 768;
  const isTablet = w > 768 && w <= 1024;

  // POSITION VALUES
  let topY, rightY, bottomY, leftY;
  let leftX, rightX, bottomX = 0;
  let scaleTop, scaleSide, scaleBottom;

  if (isMobile) {
    // MOBILE
    topY = -h * 0.20;
    rightY = -h * 0.08;
    bottomY = h * 0.18;
    leftY = -h * 0.08; // fix

    leftX = -w * 0.40;
    rightX = w * 0.42;

    bottomX = -50; // optional slight left

    scaleTop = 0.9;
    scaleSide = 0.6;
    scaleBottom = 0.65;

  } else if (isTablet) {
    //  TABLET
    topY = -h * 0.22;
    rightY = -h * 0.15;
    bottomY = h * 0.26;
    leftY = h * 0.22;

    leftX = -w * 0.32;
    rightX = w * 0.32;

    bottomX = -30; // slight left

    scaleTop = 1.0;
    scaleSide = 0.7;
    scaleBottom = 0.7;

  } else {
    //  DESKTOP
    topY = -h * 0.31;
    rightY = -h * 0.19;
    bottomY = h * 0.40;
    leftY = -h * 0.15;

    leftX = -w * 0.40;
    rightX = w * 0.40;

    bottomX = -190; // EXACT 50px LEFT SHIFT

    scaleTop = 1.0;
    scaleSide = 0.8;
    scaleBottom = 0.75;
  }

  return [
    // 🔝 TOP
    { x: 0, y: topY, s: scaleTop, rot: -8 },

    // 👉 RIGHT
    { x: rightX, y: rightY, s: scaleSide, rot: 10 },

    // 🔻 BOTTOM (✅ updated)
    { x: bottomX, y: bottomY, s: scaleBottom, rot: 2 },

    // 👈 LEFT
    { x: leftX, y: leftY, s: scaleSide, rot: -12 },
  ];
}, []);

  const isBehindSlot = (slotIndex) =>
    slotIndex !== 2 && slotIndex !== 1;

  const setLayer = useCallback((wrapEl, slotIndex) => {
    const parent = isBehindSlot(slotIndex)
      ? behindLayerRef.current
      : frontLayerRef.current;

    if (wrapEl && parent && wrapEl.parentNode !== parent) {
      parent.appendChild(wrapEl);
    }
  }, []);

  const snapToStep = useCallback(
    (stepIndex) => {
      const slots = getSlots();
      if (!slots.length) return;

      wrapsRef.current.forEach((wrapEl, i) => {
        if (!wrapEl || !cardsRef.current[i]) return;

        const slotIndex = (i + stepIndex) % 4;
        const slot = slots[slotIndex];

        setLayer(wrapEl, slotIndex);

        gsap.set(cardsRef.current[i], {
          x: slot.x,
          y: slot.y,
          scale: slot.s,
          rotation: slot.rot,
          autoAlpha: 1,
        });

        gsap.set(wrapEl, { x: 0, y: 0 });
      });
    },
    [getSlots, setLayer]
  );

  const swapTime = (fromSlot, toSlot) => {
    const moveDur = 1.05;
    if (fromSlot === 2 && toSlot === 3) return moveDur * 0.15;
    if (fromSlot === 3 && toSlot !== 3) return moveDur * 0.26;
    return moveDur * 0.45;
  };

  // ✅ MAIN ANIMATION
  useLayoutEffect(() => {
    if (animationImages.length < 4 || !bannerRef.current) return;

    const tl = gsap.timeline({
      repeat: -1,
      defaults: { ease: "power3.inOut" },
      repeatRefresh: true,
    });

    timelineRef.current = tl;

    const slowerFactor = 2; // smoother

    for (let step = 0; step < 4; step++) {
      const nextStep = (step + 1) % 4;

      tl.to(cardsRef.current, {
        duration: 1.2 * slowerFactor,
        x: (i) => getSlots()[(i + nextStep) % 4].x,
        y: (i) => getSlots()[(i + nextStep) % 4].y,
        scale: (i) => getSlots()[(i + nextStep) % 4].s,
        rotation: (i) => getSlots()[(i + nextStep) % 4].rot,
      });

      wrapsRef.current.forEach((wrapEl, i) => {
        const fromSlot = (i + step) % 4;
        const toSlot = (i + nextStep) % 4;

        if (isBehindSlot(fromSlot) !== isBehindSlot(toSlot)) {
          const at = swapTime(fromSlot, toSlot) * slowerFactor;

          tl.to(
            cardsRef.current[i],
            { duration: 0.08, autoAlpha: 0 },
            `<+${at}`
          );

          tl.call(() => setLayer(wrapEl, toSlot), null, `<`);

          tl.to(
            cardsRef.current[i],
            { duration: 0.08, autoAlpha: 1 },
            `<`
          );
        }
      });

      tl.to({}, { duration: 2.5 * slowerFactor });
    }

    snapToStep(0);

    return () => tl.kill();
  }, [animationImages, snapToStep, getSlots, setLayer]);

  // ✅ RETURN
  return {
    bannerRef,
    behindLayerRef,
    frontLayerRef,
    wrapsRef,
    cardsRef,
    animationImages,
    logoUrl,
  };
};